import { CompanyWithRolesAndWorkCentersDTO, RoleDTO, UserDTO } from '../interfaces/base-entity';
import { SESSION_USER_STORAGE_KEY } from './session-user.util';
import {
  resolveCompanyId,
  resolveWorkCenterId,
  selectBestTenantRole,
  TENANT_ROLE_PRIORITY
} from './tenant-context.util';
import { transformBackendDataToCompanyList } from './utils';

export { selectBestTenantRole, TENANT_ROLE_PRIORITY } from './tenant-context.util';

export interface SessionTenantContext {
  companyList: CompanyWithRolesAndWorkCentersDTO[];
  company: CompanyWithRolesAndWorkCentersDTO;
  role: RoleDTO;
  workCenterId: number | null;
}

export type TenantStorageLike = {
  getItem(key: string, secure?: boolean): unknown;
  setItem?(key: string, value: unknown, secure?: boolean): void;
  remove(key: string): void;
};

export function selectBestTenantCompany(
  companyList: CompanyWithRolesAndWorkCentersDTO[] | null | undefined
): CompanyWithRolesAndWorkCentersDTO | null {
  const validCompanies = (companyList ?? []).filter(company => company?.companyId != null && company.companyId > 0);
  if (!validCompanies.length) {
    return null;
  }

  const companiesWithRoles = validCompanies
    .map(company => ({ company, role: selectBestTenantRole(company.roles) }))
    .filter((item): item is { company: CompanyWithRolesAndWorkCentersDTO; role: RoleDTO } => item.role != null);

  if (companiesWithRoles.length) {
    return companiesWithRoles.reduce((best, item) =>
      (TENANT_ROLE_PRIORITY[item.role.roleId] ?? 0) > (TENANT_ROLE_PRIORITY[best.role.roleId] ?? 0) ? item : best
    ).company;
  }

  return (
    validCompanies.find(company => !!company.logoUrl?.trim()) ??
    validCompanies.find(company => !!company.companyName?.trim()) ??
    validCompanies[0]
  );
}

export type TenantPreference = {
  companyId?: number | null;
  roleId?: number | null;
  workCenterId?: number | null;
};

/** Lee la preferencia de tenant ya persistida (p. ej. tras "Cambiar compañía"). */
export function readTenantPreference(storage: TenantStorageLike): TenantPreference {
  const currentCompany = storage.getItem('currentCompany', false) as CompanyWithRolesAndWorkCentersDTO | null;
  const currentRole = storage.getItem('currentRole', false) as RoleDTO | null;
  const rawWc = storage.getItem('selectedWorkCenterId', false);
  const workCenterId =
    typeof rawWc === 'number'
      ? rawWc
      : rawWc != null && rawWc !== ''
        ? parseInt(String(rawWc), 10)
        : null;

  return {
    companyId: resolveCompanyId(currentCompany),
    roleId: currentRole?.roleId ?? null,
    workCenterId: workCenterId != null && !isNaN(workCenterId) && workCenterId > 0 ? workCenterId : null
  };
}

export function resolveTenantContextFromCompanyList(
  companyList: CompanyWithRolesAndWorkCentersDTO[] | null | undefined,
  preferred?: TenantPreference | null
): SessionTenantContext | null {
  if (!companyList?.length) {
    return null;
  }

  const preferredCompanyId = preferred?.companyId;
  const preferredCompany =
    preferredCompanyId != null && preferredCompanyId > 0
      ? companyList.find(company => company.companyId === preferredCompanyId) ?? null
      : null;

  const company = preferredCompany ?? selectBestTenantCompany(companyList);
  if (!company) {
    return null;
  }

  const preferredRoleId = preferred?.roleId;
  const preferredRole =
    preferredRoleId != null && preferredRoleId > 0
      ? (company.roles ?? []).find(role => role.roleId === preferredRoleId) ?? null
      : null;

  const role = preferredRole ?? selectBestTenantRole(company.roles);
  if (!role) {
    return null;
  }

  return {
    companyList,
    company,
    role,
    workCenterId: resolveWorkCenterId(company, role, preferred?.workCenterId)
  };
}

export function resolveSessionTenantContext(
  user: UserDTO | null | undefined,
  preferred?: TenantPreference | null
): SessionTenantContext | null {
  if (!user) {
    return null;
  }

  return resolveTenantContextFromCompanyList(transformBackendDataToCompanyList(user), preferred);
}

export function clearTenantContext(storage: TenantStorageLike): void {
  storage.remove('companyList');
  storage.remove('currentCompany');
  storage.remove('currentRole');
  storage.remove('selectedWorkCenterId');
}

export function persistTenantContext(storage: TenantStorageLike, context: SessionTenantContext): void {
  storage.setItem?.('companyList', context.companyList, false);
  storage.setItem?.('currentCompany', context.company, false);
  storage.setItem?.('currentRole', context.role, false);
  if (context.workCenterId) {
    storage.setItem?.('selectedWorkCenterId', context.workCenterId, false);
  } else {
    storage.remove('selectedWorkCenterId');
  }
}

export function hasValidTenantContext(storage: TenantStorageLike): boolean {
  const companyList = storage.getItem('companyList', false) as CompanyWithRolesAndWorkCentersDTO[] | null;
  const currentCompany = storage.getItem('currentCompany', false) as CompanyWithRolesAndWorkCentersDTO | null;
  const currentRole = storage.getItem('currentRole', false) as RoleDTO | null;

  const hasCompanyInList = (companyList ?? []).some(
    company => company?.companyId != null && company.companyId > 0
  );
  const companyId = resolveCompanyId(currentCompany);
  const roleId = currentRole?.roleId;

  return !!(hasCompanyInList && companyId && roleId);
}

export function hasAppSessionUser(
  storage: TenantStorageLike,
  liveUser?: UserDTO | null
): boolean {
  if (liveUser && (liveUser.identityId || liveUser.oidB2C || liveUser.email)) {
    return true;
  }

  const stored = storage.getItem(SESSION_USER_STORAGE_KEY, true) as UserDTO | null;
  return !!(stored && (stored.identityId || stored.oidB2C || stored.email));
}

/**
 * Indica si aún no terminó checkUserAuth y no debe mostrarse
 * ni el portal ni la pantalla de “sin compañía”.
 */
export function isTenantSessionBootstrapping(options?: {
  msalHasAccount?: boolean;
  resolutionPending?: boolean;
}): boolean {
  return !!options?.resolutionPending && !!options?.msalHasAccount;
}

/**
 * Bloquea el portal cuando hay sesión (app o MSAL) pero no hay compañía+rol válidos.
 * Solo aplica cuando la resolución ya terminó (no durante el bootstrap).
 */
export function isTenantContextBlocked(
  storage: TenantStorageLike,
  options?: {
    liveUser?: UserDTO | null;
    msalHasAccount?: boolean;
    resolutionPending?: boolean;
  }
): boolean {
  if (isTenantSessionBootstrapping(options)) {
    return false;
  }

  const hasSession =
    hasAppSessionUser(storage, options?.liveUser) || !!options?.msalHasAccount;
  if (!hasSession) {
    return false;
  }

  return !hasValidTenantContext(storage);
}
